import singletonRouter from "next/router";
import { Configure, InstantSearch, InstantSearchServerState, InstantSearchSSRProvider } from "react-instantsearch";
import { createInstantSearchRouterNext } from "react-instantsearch-router-nextjs";

import { searchClient } from "~/components/search/search-client";
import Algolia from "~/types/algolia";
import CommonUtils from "~/utils/common-utils";

import AlgoliaProductListing from "./algolia-product-listing";
import Facets from "./facets";

type Props = {
  serverState?: InstantSearchServerState;
  algoliaIndexId: string;
  components?: any;
  listScope: string;
  serverUrl: string;
  algoliaUserToken: string;
  categoryId: string;
  isPaginationMode?: boolean;
  products?: any[];
  pageNumber?: number;
  onTotalChange?: (total: number) => void;
};

export default function ProductGridSkeleton(props: Props) {
  const { serverState, serverUrl, algoliaUserToken, categoryId, components, listScope, algoliaIndexId } = props;

  const categoryLevel = categoryId.split("_").length;

  const algoliaPLPConfig = CommonUtils.parseJsonSafely(
    process.env.NEXT_PUBLIC_ALGOLIA_PLP_CONFIG!
  ) as Algolia.Configuration;

  return (
    <InstantSearchSSRProvider {...serverState}>
      <InstantSearch
        searchClient={searchClient}
        indexName={algoliaIndexId}
        future={{
          preserveSharedStateOnUnmount: true,
        }}
        insights
        routing={{
          stateMapping: {
            stateToRoute: (uiState: any): any => {
              const indexUiState = uiState[algoliaIndexId];
              const { refinementList, range, ratingMenu, sortBy, ...rest } = indexUiState;
              const queryParams: Record<string, string> = {};

              if (serverState) {
                const params = new URLSearchParams(window.location.search);
                params.forEach((value, key) => {
                  if (!key.includes(algoliaIndexId)) {
                    queryParams[key] = value;
                  }
                });
              }

              const { category, ...extraQueryParams } = queryParams;

              return {
                [algoliaIndexId]: {
                  refinementList,
                  range,
                  ratingMenu,
                  sortBy,
                },
                ...extraQueryParams,
              };
            },
            routeToState(routeState) {
              if (!routeState?.[algoliaIndexId]) {
                return routeState;
              }

              const { refinementList, range, ratingMenu, sortBy, ...rest } = routeState[algoliaIndexId];

              return {
                [algoliaIndexId]: {
                  refinementList,
                  range,
                  ratingMenu,
                  sortBy,
                },
              };
            },
          },
          router: createInstantSearchRouterNext({
            singletonRouter,
            serverUrl,
            routerOptions: { cleanUrlOnDispose: false },
          }),
        }}
      >
        <Configure
          {...algoliaPLPConfig}
          userToken={algoliaUserToken}
          disjunctiveFacetsRefinements={{ [`categories.lvl${categoryLevel}`]: [categoryId] }}
          page={props.isPaginationMode && props.pageNumber ? props.pageNumber - 1 : 0}
        />
        <Facets />
        {serverState && (
          <AlgoliaProductListing
            components={components}
            listScope={listScope}
            categoryId={props.categoryId}
            initialResultsHits={serverState.initialResults[algoliaIndexId].results?.[0].hits || []}
            algoliaUserToken={algoliaUserToken}
            isPaginationMode={props.isPaginationMode}
            staticProducts={props.products}
            onTotalChange={props.onTotalChange}
          />
        )}
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
}
